import React, { useMemo } from 'react';

import { useIntl } from 'react-intl-next';

import AtlaskitForm from '@atlaskit/form';
import Heading from '@atlaskit/heading';
import { Box, Stack, xcss } from '@atlaskit/primitives';
import { type ValueType } from '@atlaskit/select';
import TagGroup from '@atlaskit/tag-group';
import {
	CancelButton,
	Card,
	CloseButton,
	Footer,
	OverrideButton,
	type RequestedResponder,
	useCallPromise,
} from '@atlassian/eoc-schedule-common';

import { useCurrentUser } from '../../../../utils/hooks/current-user';
import { messages as commonMessages } from '../../../messages';
import { type AutoCompleteWithNoOneItem } from '../../auto-complete-with-noone/custom-option/types';
import { OverridenWith } from '../overriden-with';
import { ResponderTag } from '../responder-tag';

import { type FormFields, type MultiQuickOverrideShiftCardProps } from './types';

const formStyles = xcss({
	margin: '0',
	marginTop: 'space.negative.200',
});

const MultiQuickOverrideShiftCard = ({
	cards,
	onClose,
	onCancel,
	onAddOverride,
	isDisabled,
}: MultiQuickOverrideShiftCardProps) => {
	const intlApi = useIntl();

	const current = useCurrentUser();

	const { isLoading: isAddOverrideLoading, call: addOverride } = useCallPromise(onAddOverride);

	const responders = useMemo(
		() =>
			Object.values(
				cards.reduce(
					(acc, { responder }): Record<string, RequestedResponder> =>
						acc[responder.id] ? acc : { ...acc, [responder.id]: responder },
					{} as Record<string, RequestedResponder>,
				),
			),
		[cards],
	);

	return (
		<Card
			role="dialog"
			aria-labelledby="shift-card-title"
			aria-describedby="shift-card-description"
			controls={<CloseButton onClick={onClose} />}
			content={
				<Stack space="space.200">
					<Heading id="shift-card-title" as="h2" size="xsmall">
						{intlApi.formatMessage(commonMessages.quickOverrideTitle)}
					</Heading>

					<Stack space="space.0">
						<Heading id="shift-card-description" as="h3" size="xxsmall">
							{intlApi.formatMessage(commonMessages.selectionCount, {
								count: cards.length,
							})}
						</Heading>

						<TagGroup>
							{responders.map((responder) => (
								<ResponderTag responder={responder} key={responder.id} />
							))}
						</TagGroup>
					</Stack>

					<AtlaskitForm<FormFields> onSubmit={({ responder }) => addOverride(responder)}>
						{({ formProps }) => (
							<Box as="form" {...formProps} xcss={formStyles}>
								<Stack space="space.200">
									<OverridenWith
										isLoading={current.isLoading}
										isDisabled={isDisabled}
										defaultValue={current.user as ValueType<AutoCompleteWithNoOneItem, false>}
									/>

									<Footer>
										<CancelButton onClick={onCancel} />
										<OverrideButton isLoading={isAddOverrideLoading} type="submit" />
									</Footer>
								</Stack>
							</Box>
						)}
					</AtlaskitForm>
				</Stack>
			}
		/>
	);
};

export { MultiQuickOverrideShiftCard };
